// src/components/layout/Dashboard/Orders/OrderDetailPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  MoreVertical,
  Package,
  Calendar,
  Hash,
  IndianRupee,
  Utensils,
  Plus,
  Trash2,
  Wallet,
  Users,
  User,
  Store,
  Bike,
  MapPin,
  CheckCircle2,
  Clock,
  Navigation,
  FileText,
  ChevronDown,
  Save,
  XCircle,
  Loader,
  Maximize2,
  Minimize2,
  CreditCard,
  Image,
  Camera,
  Truck,
  TrendingDown,
  Receipt,
  Shield,
  Zap,
  Star,
  Heart,
  Phone,
  Mail,
  Building,
  UserCircle,
} from 'lucide-react';
import { getData, postData, serverURL } from '../../../../Services/Fetchnodeservices';

const OrderDetailPage = ({ darkMode = false, isDark }) => {
  // ✅ Add this line
  const effectiveDark = isDark !== undefined ? isDark : darkMode;
  
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const orderFromState = location.state?.order;




  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    status: true,
    amounts: true,
    items: true,
    people: true,
    delivery: true,
    timeline: true,
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullScreen(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', () => {
      setIsFullScreen(!!document.fullscreenElement);
    });
    return () => {
      document.removeEventListener('fullscreenchange', () => {});
    };
  }, []);

 const theme = {
    bg: effectiveDark ? '#0f1724' : '#f1f5f9',
    surface: effectiveDark ? '#1a2332' : '#ffffff',
    surfaceAlt: effectiveDark ? '#243044' : '#f8fafc',
    border: effectiveDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0',
    text: effectiveDark ? '#f1f5f9' : '#0f172a',
    textMuted: effectiveDark ? '#64748b' : '#94a3b8',
    textSub: effectiveDark ? '#94a3b8' : '#64748b',
    primary: '#6366f1',
    primaryLight: '#818cf8',
    secondary: '#8b5cf6',
    success: '#22c55e',
    successLight: '#4ade80',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    accent: '#ec4899',
  };

  // Fetch order data
  useEffect(() => {
    if (orderFromState) {
      setOrderData(formatOrderData(orderFromState));
      setLoading(false);
    } else if (id) {
      fetchOrderData();
    } else {
      setLoading(false);
    }
  }, [id, orderFromState]);

  const formatOrderData = (order) => {
    let parsedItems = [];
    try {
      const itemsData = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
      parsedItems = Array.isArray(itemsData) ? itemsData.map(item => ({
        id: item.id || item.product_id || Math.random(),
        name: item.name || 'Unknown Item',
        quantity: parseInt(item.qty) || 1,
        price: parseFloat(item.price) || 0,
        total: (parseInt(item.qty) || 1) * (parseFloat(item.price) || 0),
        image: item.pic || getItemImage(item.name),
        variant: item.variant || item.variant_name || '',
        product_id: item.product_id || null,
        vendor_price: item.vendor_price || null,
        menu_price: item.menu_price || null,
      })) : [];
    } catch (e) {
      parsedItems = [{
        id: 1,
        name: 'Unknown Item',
        quantity: 1,
        price: 0,
        total: 0,
        image: null,
        variant: '',
      }];
    }

    // Handle delivery pickup and dropoff images
    let pickupPic = null;
    let dropoffPic = null;
    
    if (order.delivery_pickup_pic) {
      try {
        pickupPic = JSON.parse(order.delivery_pickup_pic);
      } catch (e) {
        pickupPic = order.delivery_pickup_pic;
      }
    }
    
    if (order.delivery_dropoff_pic) {
      try {
        dropoffPic = JSON.parse(order.delivery_dropoff_pic);
      } catch (e) {
        dropoffPic = order.delivery_dropoff_pic;
      }
    }

    return {
      id: order.id || 'N/A',
      date: order.created_at || order.order_date || 'N/A',
      items: parsedItems,
      amounts: {
        price: parseFloat(order.price) || 0,
        discount: parseFloat(order.discount) || 0,
        gst: parseFloat(order.tax) || 0,
        handlingCharge: parseFloat(order.handling_charge) || 0,
        orderReceiving: parseFloat(order.order_receiving_charges) || 0,
        finalAmount: parseFloat(order.final_amount) || 0,
        deliveryCharge: parseFloat(order.delivery_charge) || 0,
        businessAmount: parseFloat(order.business_amount) || 0,
        zaptodoorCommission: parseFloat(order.platform_amount) || 0,
        userPlatformFee: parseFloat(order.user_platform_fee) || 0,
        driverPlatformFee: parseFloat(order.driver_platform_fee) || 0,
        bizPlatformFee: parseFloat(order.business_platform_fee) || 0,
        driverAmount: parseFloat(order.driver_amount) || 0,
        driverTips: parseFloat(order.driver_tips) || 0,
        walletPay: parseFloat(order.wallet_pay) || 0,
        platformFeeAmount: parseFloat(order.platform_fee_amount) || 0,
        handlingFeeAmount: parseFloat(order.handling_fee_amount) || 0,
        receivingFeeAmount: parseFloat(order.receiving_fee_amount) || 0,
        commissionAmount: parseFloat(order.commission_amount) || 0,
      },
      people: {
        customer: {
          name: order.receiver_name || order.customer_name || 'Unknown Customer',
          username: order.userid || 'user',
          phone: order.receiver_phone || order.customer_phone || 'N/A',
        },
        restaurant: {
          name: order.organisation_name || order.restaurant || order.designation || 'Unknown Restaurant',
          id: order.designation_id || 'N/A',
          contact: order.restaurant_contact || 'N/A',
        },
        driver: {
          name: order.driver_name || order.delivery_person_id || 'Not Assigned',
          id: order.delivery_person_id || 'N/A',
          contact: order.driver_contact || 'N/A',
        },
      },
      delivery: {
        pickup: order.pickup_address || 'N/A',
        dropoff: order.dropoff_address || 'N/A',
        pickupPic: pickupPic,
        dropoffPic: dropoffPic,
        pickupLat: order.pickup_lat || null,
        pickupLng: order.pickup_lng || null,
        dropoffLat: order.dropoff_lat || null,
        dropoffLng: order.dropoff_lng || null,
      },
      timeline: {
        orderPlaced: order.created_at || 'N/A',
        restroAccepted: order.restro_accept_time || 'Pending',
        prepDuration: order.restro_prep_duration || 'N/A',
        restroReady: order.restro_ready_time || 'Pending',
        riderPickup: order.rider_pickup_time || 'Pending',
        delivered: order.order_delivered_time || 'Pending',
      },
      status: {
        restaurant: formatStatus(order.order_status_restro),
        driver: formatStatus(order.order_status_driver),
        user: formatStatus(order.order_status_user),
        pay: formatPayStatus(order.pay_status),
        payMethod: formatPayMethod(order.pay_method),
        driverId: order.delivery_person_id || 'N/A',
        pickupOtp: order.pickup_otp || 'N/A',
      },
      raw: order,
    };
  };

  const fetchOrderData = async () => {
    try {
      setLoading(true);
      const response = await getData('orders/Orders');
      if (response.status) {
        const order = response.data.find(o => o.id == id);
        if (order) {
          setOrderData(formatOrderData(order));
        } else {
          setOrderData(null);
        }
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const getItemImage = (name) => {
    const emojis = {
      'momo': '🥟',
      'biryani': '🍗',
      'pizza': '🍕',
      'burger': '🍔',
      'noodles': '🍜',
      'rice': '🍚',
      'chicken': '🍗',
      'paneer': '🧀',
      'dumplings': '🥟',
      'spring roll': '🌯',
      'default': '🍽️'
    };
    const lowerName = name?.toLowerCase() || '';
    for (const [key, emoji] of Object.entries(emojis)) {
      if (lowerName.includes(key)) return emoji;
    }
    return emojis.default;
  };

  const formatStatus = (status) => {
    if (!status) return 'Pending';
    const map = {
      'placed': 'Placed',
      'accepted': 'Accepted',
      'preparing': 'Preparing',
      'ready': 'Ready',
      'picked': 'Picked',
      'on_the_way': 'On the Way',
      'completed': 'Completed',
      'dropped': 'Dropped',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return map[status] || status;
  };

  const formatPayStatus = (status) => {
    const map = {
      'pending': 'Pending',
      'paid': 'Paid',
      'failed': 'Failed',
      'refunded': 'Refunded'
    };
    return map[status] || status || 'Pending';
  };

  const formatPayMethod = (method) => {
    const map = {
      'COD': 'COD',
      'online': 'Online',
      'wallet': 'Wallet'
    };
    return map[method] || method || 'N/A';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Placed': theme.info,
      'Accepted': theme.success,
      'Preparing': theme.warning,
      'Ready': theme.primary,
      'Picked': theme.primary,
      'On the Way': theme.primary,
      'Completed': theme.success,
      'Dropped': theme.success,
      'Delivered': theme.success,
      'Cancelled': theme.danger,
      'Pending': theme.warning,
      'Paid': theme.success,
      'Failed': theme.danger,
      'Refunded': theme.info,
      'COD': theme.success,
      'Online': theme.primary,
      'Wallet': theme.info,
    };
    return colors[status] || theme.textMuted;
  };

  // ============================================================
  // HANDLE SAVE - Submit to Edit Route
  // ============================================================
  const handleSave = async () => {
    if (!orderData) return;
    
    try {
      setSaving(true);
      setSaveMessage('');
      
      const payload = buildUpdatePayload(orderData);
      const response = await postData('order/edit', payload);
      
      if (response.status) {
        setSaveMessage('✅ Order updated successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setSaveMessage('❌ Update failed: ' + response.message);
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('❌ Error saving data');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // BUILD UPDATE PAYLOAD
  // ============================================================
  const buildUpdatePayload = (data) => {
    const raw = data.raw || {};
    
    return {
      id: data.id,
      designation: raw.designation || data.people.restaurant.name,
      designation_id: data.people.restaurant.id,
      organisation_name: data.people.restaurant.name,
      userid: data.people.customer.username,
      delivery_person_id: data.people.driver.id === 'Not Assigned' ? null : data.people.driver.id,
      driver_name: data.people.driver.name,
      driver_contact: data.people.driver.contact,
      restaurant_contact: data.people.restaurant.contact,
      items: JSON.stringify(data.items.map(item => ({
        id: item.id,
        name: item.name,
        qty: item.quantity,
        price: item.price,
        variant_name: item.variant || 'default',
        pic: item.image || null,
        product_id: item.product_id || null,
        vendor_price: item.vendor_price || null,
        menu_price: item.menu_price || null,
      }))),
      pay_status: data.status.pay.toLowerCase(),
      pay_method: data.status.payMethod,
      order_status_restro: data.status.restaurant.toLowerCase(),
      order_status_driver: data.status.driver?.toLowerCase() || null,
      order_status_user: data.status.user?.toLowerCase() || null,
      pickup_address: data.delivery.pickup,
      dropoff_address: data.delivery.dropoff,
      receiver_name: data.people.customer.name,
      receiver_phone: data.people.customer.phone,
      notes_for_restro: raw.notes_for_restro || null,
      price: data.amounts.price,
      discount: data.amounts.discount,
      tax: data.amounts.gst,
      handling_charge: data.amounts.handlingCharge,
      order_receiving_charges: data.amounts.orderReceiving,
      final_amount: data.amounts.finalAmount,
      delivery_charge: data.amounts.deliveryCharge,
      business_amount: data.amounts.businessAmount,
      platform_amount: data.amounts.zaptodoorCommission,
      user_platform_fee: data.amounts.userPlatformFee,
      driver_platform_fee: data.amounts.driverPlatformFee,
      business_platform_fee: data.amounts.bizPlatformFee,
      driver_amount: data.amounts.driverAmount,
      driver_tips: data.amounts.driverTips,
      wallet_pay: data.amounts.walletPay,
      platform_fee_amount: data.amounts.platformFeeAmount || 0,
      handling_fee_amount: data.amounts.handlingFeeAmount || 0,
      receiving_fee_amount: data.amounts.receivingFeeAmount || 0,
      commission_amount: data.amounts.commissionAmount || 0,
      status: raw.status !== undefined && raw.status !== null ? raw.status : '1',
      hide: raw.hide !== undefined && raw.hide !== null ? raw.hide : '0',
      sessionid: raw.sessionid || null,
      business_amount_claimed: raw.business_amount_claimed || '0',
      business_amount_status: raw.business_amount_status || '0',
      driver_amount_claimed: raw.driver_amount_claimed || '0',
      driver_amount_status: raw.driver_amount_status || '0',
      is_driver_deposit_cash: raw.is_driver_deposit_cash || '0',
      pickup_lat: raw.pickup_lat || null,
      pickup_lng: raw.pickup_lng || null,
      dropoff_lat: raw.dropoff_lat || null,
      dropoff_lng: raw.dropoff_lng || null,
      pickup_otp: raw.pickup_otp || null,
      pickup_otp_verified_at: raw.pickup_otp_verified_at || null,
      razorpay_order_id: raw.razorpay_order_id || null,
      razorpay_payment_id: raw.razorpay_payment_id || null,
      razorpay_signature: raw.razorpay_signature || null,
      restro_accept_time: raw.restro_accept_time || null,
      restro_prep_duration: raw.restro_prep_duration || null,
      restro_ready_time: raw.restro_ready_time || null,
      rider_pickup_time: raw.rider_pickup_time || null,
      rider_delivery_duration: raw.rider_delivery_duration || null,
      order_delivered_time: raw.order_delivered_time || null,
      user_to_restro_rating: raw.user_to_restro_rating || null,
      user_to_restro_remarks: raw.user_to_restro_remarks || null,
      user_to_driver_rating: raw.user_to_driver_rating || null,
      user_to_driver_remarks: raw.user_to_driver_remarks || null,
      driver_to_user_rating: raw.driver_to_user_rating || null,
      driver_to_user_remarks: raw.driver_to_user_remarks || null,
      driver_to_restro_rating: raw.driver_to_restro_rating || null,
      driver_to_restro_remarks: raw.driver_to_restro_remarks || null,
      business_cancel_remarks: raw.business_cancel_remarks || null,
      user_cancel_remarks: raw.user_cancel_remarks || null,
      driver_cancel_remarks: raw.driver_cancel_remarks || null,
      business_cancel_time: raw.business_cancel_time || null,
      user_cancel_time: raw.user_cancel_time || null,
      driver_cancel_time: raw.driver_cancel_time || null,
      refund_status: raw.refund_status || null,
      refund_amount: raw.refund_amount || null,
      refund_id: raw.refund_id || null,
      refund_time: raw.refund_time || null,
      declined_by: raw.declined_by || null,
      driver_lat: raw.driver_lat || null,
      driver_lng: raw.driver_lng || null,
      delivery_pickup_pic: raw.delivery_pickup_pic || null,
      delivery_dropoff_pic: raw.delivery_dropoff_pic || null,
    };
  };

  // ============================================================
  // HANDLE CHANGE FUNCTIONS
  // ============================================================
  const handleAmountChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      amounts: {
        ...prev.amounts,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleStatusChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      status: {
        ...prev.status,
        [field]: value
      }
    }));
  };

  const handleCustomerChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      people: {
        ...prev.people,
        customer: {
          ...prev.people.customer,
          [field]: value
        }
      }
    }));
  };

  const handleRestaurantChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      people: {
        ...prev.people,
        restaurant: {
          ...prev.people.restaurant,
          [field]: value
        }
      }
    }));
  };

  const handleDriverChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      people: {
        ...prev.people,
        driver: {
          ...prev.people.driver,
          [field]: value
        }
      }
    }));
  };

  const handleDeliveryChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      delivery: {
        ...prev.delivery,
        [field]: value
      }
    }));
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      name: 'New Item',
      quantity: 1,
      price: 0,
      total: 0,
      image: null,
      variant: '',
    };
    setOrderData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const removeItem = (id) => {
    setOrderData((prev) => ({ ...prev, items: prev.items.filter((item) => item.id !== id) }));
  };

  const updateItem = (id, field, value) => {
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'price') {
            updated.total = (updated.quantity || 1) * (updated.price || 0);
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  // ============================================================
  // COMPONENTS
  // ============================================================
  const SectionHeader = ({ title, icon: Icon, section, children }) => {
    const isExpanded = expandedSections[section];

    return (
      <div style={{
        backgroundColor: theme.surface,
        borderRadius: '12px',
        marginBottom: '12px',
        overflow: 'hidden',
        border: `1px solid ${theme.border}`,
      }}>
        <div
          onClick={() => toggleSection(section)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: isExpanded ? `1px solid ${theme.border}` : 'none',
            cursor: 'pointer',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.surfaceAlt}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.primary + '12',
              color: theme.primary,
            }}>
              <Icon size={16} />
            </div>
            <span style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>
              {title}
            </span>
          </div>
          <div style={{
            transition: 'transform 0.2s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            color: theme.textMuted,
          }}>
            <ChevronDown size={18} />
          </div>
        </div>
        {isExpanded && <div style={{ padding: '16px 18px' }}>{children}</div>}
      </div>
    );
  };

  const StatusDropdown = ({ status, path, options, label }) => {
    const color = getStatusColor(status);

    return (
      <div>
        <div style={{ 
          fontSize: '10px', 
          fontWeight: '600', 
          color: theme.textMuted, 
          marginBottom: '6px', 
          textTransform: 'uppercase', 
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {label}
        </div>
        <select
          value={status}
          onChange={(e) => handleStatusChange(path, e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: '20px',
            border: `1px solid ${color}25`,
            backgroundColor: color + '12',
            color: color,
            fontSize: '13px',
            fontWeight: '600',
            outline: 'none',
            width: '100%',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            appearance: 'auto',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = `0 4px 12px ${color}30`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {options.map((opt) => (
            <option key={opt} value={opt} style={{ backgroundColor: theme.surface, color: theme.text }}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // ============================================================
  // LOADING AND ERROR STATES
  // ============================================================
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.bg,
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={40} color={theme.primary} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: theme.textMuted, marginTop: '12px' }}>Loading order...</p>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.bg,
      }}>
        <div style={{ textAlign: 'center' }}>
          <XCircle size={48} color={theme.danger} />
          <p style={{ color: theme.text, marginTop: '12px', fontSize: '18px', fontWeight: '600' }}>
            Order not found
          </p>
          <button
            onClick={() => navigate('/Dashboard/orders')}
            style={{
              marginTop: '16px',
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: theme.primary,
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const timelineEvents = [
    { label: 'Order Placed', time: orderData.timeline.orderPlaced, icon: Clock, status: orderData.timeline.orderPlaced !== 'N/A' ? 'completed' : 'pending' },
    { label: 'Restaurant Accepted', time: orderData.timeline.restroAccepted, icon: CheckCircle2, status: orderData.timeline.restroAccepted !== 'Pending' ? 'completed' : 'pending' },
    { label: 'Preparation Time', time: orderData.timeline.prepDuration, icon: Clock, status: orderData.timeline.prepDuration !== 'N/A' ? 'active' : 'pending' },
    { label: 'Restaurant Ready', time: orderData.timeline.restroReady, icon: CheckCircle2, status: orderData.timeline.restroReady !== 'Pending' ? 'completed' : 'pending' },
    { label: 'Rider Pickup', time: orderData.timeline.riderPickup, icon: Bike, status: orderData.timeline.riderPickup !== 'Pending' ? 'completed' : 'pending' },
    { label: 'Delivered', time: orderData.timeline.delivered, icon: CheckCircle2, status: orderData.timeline.delivered !== 'Pending' ? 'completed' : 'pending' },
  ];

  // Image render helper
  const renderImage = (imageData, fallbackIcon, altText) => {
    if (!imageData) {
      return (
        <div style={{
          width: '100%',
          height: '180px',
          backgroundColor: theme.surfaceAlt,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px dashed ${theme.border}`,
          flexDirection: 'column',
          gap: '8px',
        }}>
          {fallbackIcon}
          <span style={{ fontSize: '12px', color: theme.textMuted }}>No image available</span>
        </div>
      );
    }

    let imageUrl = imageData;
    if (typeof imageData === 'object') {
      imageUrl = imageData.url || imageData.path || imageData;
    }

    // Check if it's a full URL or needs serverURL
    const fullUrl = imageUrl?.startsWith('http') ? imageUrl : `${serverURL}${imageUrl}`;

    return (
      <div style={{
        width: '100%',
        height: '180px',
        borderRadius: '10px',
        overflow: 'hidden',
        border: `1px solid ${theme.border}`,
        position: 'relative',
        backgroundColor: theme.surfaceAlt,
      }}>
        <img
          src={fullUrl}
          alt={altText}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `
              <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px;background:${theme.surfaceAlt};">
                <span style="font-size:40px;">📷</span>
                <span style="font-size:12px;color:${theme.textMuted}">Image not available</span>
              </div>
            `;
          }}
        />
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          backgroundColor: 'rgba(0,0,0,0.6)',
          padding: '4px 10px',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#fff',
          fontWeight: '500',
        }}>
          {altText}
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <div style={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: theme.bg,
        color: theme.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px',
          backgroundColor: theme.surface,
          borderBottom: `1px solid ${theme.border}`,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}>
          <button
            type="button"
            onClick={() => navigate('/Dashboard/orders')}
            style={{
              padding: '8px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              color: theme.text,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.primary;
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.borderColor = theme.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = theme.text;
              e.currentTarget.style.borderColor = theme.border;
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.text }}>
              Order Details
            </div>
            <div style={{ fontSize: '12px', fontWeight: '500', color: theme.textMuted }}>
              <Hash size={12} style={{ display: 'inline', marginRight: '4px' }} />
              #{orderData.id}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {saveMessage && (
              <span style={{
                fontSize: '13px',
                fontWeight: '600',
                padding: '6px 14px',
                borderRadius: '8px',
                backgroundColor: saveMessage.includes('✅') ? theme.success + '15' : theme.danger + '15',
                color: saveMessage.includes('✅') ? theme.success : theme.danger,
              }}>
                {saveMessage}
              </span>
            )}
            {saving && <Loader size={20} color={theme.primary} style={{ animation: 'spin 1s linear infinite' }} />}
            <button
              type="button"
              onClick={toggleFullScreen}
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: `1px solid ${theme.border}`,
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: theme.text,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.primary;
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = theme.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = theme.text;
                e.currentTarget.style.borderColor = theme.border;
              }}
              title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
            >
              {isFullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button
              type="button"
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: `1px solid ${theme.border}`,
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: theme.text,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.primary;
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = theme.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = theme.text;
                e.currentTarget.style.borderColor = theme.border;
              }}
            >
              <MoreVertical size={18} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ 
          maxWidth: isFullScreen ? '100%' : '1200px', 
          margin: '0 auto', 
          padding: isFullScreen ? '16px 32px 40px' : '16px 24px 40px',
          width: '100%',
        }}>
          
          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <div style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '500', color: theme.textMuted, marginBottom: '4px' }}>
                Order ID
              </div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: theme.primary }}>
                #{orderData.id}
              </div>
            </div>
            <div style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '500', color: theme.textMuted, marginBottom: '4px' }}>
                Date
              </div>
              <div style={{ fontSize: '13px', fontWeight: '600' }}>
                {orderData.date}
              </div>
            </div>
            <div style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '500', color: theme.textMuted, marginBottom: '4px' }}>
                Items
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: theme.primary }}>
                {orderData.items.length}
              </div>
            </div>
            <div style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '500', color: theme.textMuted, marginBottom: '4px' }}>
                Total Amount
              </div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: theme.primary }}>
      ₹{Number(orderData?.amounts?.finalAmount || 0).toFixed(2)}
              </div>
            </div>
            <div style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '500', color: theme.textMuted, marginBottom: '4px' }}>
                Pickup OTP
              </div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: theme.warning, letterSpacing: '4px' }}>
                {orderData.status.pickupOtp}
              </div>
            </div>
            <div style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '500', color: theme.textMuted, marginBottom: '4px' }}>
                Driver ID
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>
                {orderData.status.driverId}
              </div>
            </div>
          </div>

          {/* ============================================================ */}
          {/* ORDER STATUS SECTION */}
          {/* ============================================================ */}
          <SectionHeader title="Order Status" icon={CheckCircle2} section="status">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px',
            }}>
              <StatusDropdown 
                status={orderData.status.restaurant} 
                path="restaurant"
                options={['Placed', 'Accepted', 'Preparing', 'Ready', 'Picked', 'On the Way', 'Completed', 'Dropped', 'Delivered', 'Cancelled']}
                label={<><Store size={14} color={theme.warning} style={{ marginRight: '4px' }} /> Restaurant Status</>}
              />
              <StatusDropdown 
                status={orderData.status.driver} 
                path="driver"
                options={['Placed', 'Accepted', 'Preparing', 'Ready', 'Picked', 'On the Way', 'Completed', 'Dropped', 'Delivered', 'Cancelled']}
                label={<><Bike size={14} color={theme.success} style={{ marginRight: '4px' }} /> Driver Status</>}
              />
              <StatusDropdown 
                status={orderData.status.user} 
                path="user"
                options={['Placed', 'Accepted', 'Preparing', 'Ready', 'Picked', 'On the Way', 'Completed', 'Dropped', 'Delivered', 'Cancelled']}
                label={<><User size={14} color={theme.info} style={{ marginRight: '4px' }} /> User Status</>}
              />
              <StatusDropdown 
                status={orderData.status.pay} 
                path="pay"
                options={['Pending', 'Paid', 'Failed', 'Refunded']}
                label={<><CreditCard size={14} color={theme.primary} style={{ marginRight: '4px' }} /> Pay Status</>}
              />
              <StatusDropdown 
                status={orderData.status.payMethod} 
                path="payMethod"
                options={['COD', 'Online', 'Wallet']}
                label={<><Wallet size={14} color={theme.warning} style={{ marginRight: '4px' }} /> Pay Method</>}
              />
            </div>
          </SectionHeader>

          {/* ============================================================ */}
          {/* AMOUNTS SECTION - WITH ADDITIONAL BREAKDOWN */}
          {/* ============================================================ */}
       <SectionHeader title="Amounts" icon={Wallet} section="amounts">
  <div style={{
    backgroundColor: theme.surface,
    borderRadius: '16px',
    overflow: 'hidden',
    border: `1px solid ${theme.border}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  }}>
    {/* Main Amounts Grid - 2 Columns */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0',
    }}>
      {/* Price */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderRight: `1px solid ${theme.border}`,
        backgroundColor: theme.surface,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.primary + '06';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.surface;
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: theme.primary + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IndianRupee size={14} color={theme.primary} />
          </div>
          <span style={{ fontSize: '13px', color: theme.textSub, fontWeight: '500' }}>Price</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.price}
          onChange={(e) => handleAmountChange('price', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '700',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '85px',
            textAlign: 'right',
            padding: '4px 6px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.primary;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Discount */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        backgroundColor: theme.surface,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.success + '06';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.surface;
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: theme.success + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <TrendingDown size={14} color={theme.success} />
          </div>
          <span style={{ fontSize: '13px', color: theme.textSub, fontWeight: '500' }}>Discount</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.discount}
          onChange={(e) => handleAmountChange('discount', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '700',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.success,
            outline: 'none',
            width: '85px',
            textAlign: 'right',
            padding: '4px 6px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.success;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* GST */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderTop: `1px solid ${theme.border}`,
        borderRight: `1px solid ${theme.border}`,
        backgroundColor: theme.surface,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.warning + '06';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.surface;
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: theme.warning + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Receipt size={14} color={theme.warning} />
          </div>
          <span style={{ fontSize: '13px', color: theme.textSub, fontWeight: '500' }}>GST</span>
        </div>
        <input
          type="number"
          value={parseFloat(orderData?.amounts?.gst || 0).toFixed(2)}
          onChange={(e) => handleAmountChange('gst', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '700',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '85px',
            textAlign: 'right',
            padding: '4px 6px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.warning;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Handling Charge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderTop: `1px solid ${theme.border}`,
        backgroundColor: theme.surface,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.info + '06';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.surface;
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: theme.info + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Package size={14} color={theme.info} />
          </div>
          <span style={{ fontSize: '13px', color: theme.textSub, fontWeight: '500' }}>Handling Charge</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.handlingCharge}
          onChange={(e) => handleAmountChange('handlingCharge', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '700',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '85px',
            textAlign: 'right',
            padding: '4px 6px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.info;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* ✅ Platform Fee - NEW (Handling Charge के नीचे) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderTop: `1px solid ${theme.border}`,
        borderRight: `1px solid ${theme.border}`,
        backgroundColor: theme.surface,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.primary + '06';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.surface;
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: theme.primary + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Shield size={14} color={theme.primary} />
          </div>
          <span style={{ fontSize: '13px', color: theme.textSub, fontWeight: '500' }}>Platform Fee</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.platformFeeAmount}
          onChange={(e) => handleAmountChange('platformFeeAmount', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '700',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '85px',
            textAlign: 'right',
            padding: '4px 6px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.primary;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Order Receiving - Highlighted */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderTop: `1px solid ${theme.border}`,
        background: `linear-gradient(135deg, ${theme.primary}08 0%, ${theme.primary}04 100%)`,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `linear-gradient(135deg, ${theme.primary}12 0%, ${theme.primary}08 100%)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = `linear-gradient(135deg, ${theme.primary}08 0%, ${theme.primary}04 100%)`;
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 10px ${theme.primary}30`,
          }}>
            <Receipt size={14} color="#fff" />
          </div>
          <span style={{ fontSize: '13px', color: theme.text, fontWeight: '600' }}>Order Receiving</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.orderReceiving}
          onChange={(e) => handleAmountChange('orderReceiving', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '700',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.primary}40`,
            color: theme.primary,
            outline: 'none',
            width: '85px',
            textAlign: 'right',
            padding: '4px 6px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.primary;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.primary + '40';
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Final Amount - PROMINENT */}
     <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderTop: `1px solid ${theme.border}`,
        backgroundColor: theme.surface,
        transition: 'all 0.2s ease',
        cursor: 'text',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.accent + '06';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.surface;
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: theme.accent + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Truck size={14} color={theme.accent} />
          </div>
          <span style={{ fontSize: '13px', color: theme.textSub, fontWeight: '500' }}>Delivery Charge</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.deliveryCharge}
          onChange={(e) => handleAmountChange('deliveryCharge', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '700',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '85px',
            textAlign: 'right',
            padding: '4px 6px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.accent;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Delivery Charge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 18px',
        borderTop: `1px solid ${theme.border}`,
        borderRight: `1px solid ${theme.border}`,
        background: `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}08 100%)`,
        transition: 'all 0.2s ease',
        cursor: 'text',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `linear-gradient(135deg, ${theme.primary}20 0%, ${theme.primary}12 100%)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}08 100%)`;
      }}>
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.primary}20 0%, transparent 70%)`,
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', zIndex: 1 }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 6px 14px ${theme.primary}40`,
          }}>
            <Zap size={16} color="#fff" />
          </div>
          <span style={{ fontSize: '14px', fontWeight: '800', color: theme.primary, letterSpacing: '-0.2px' }}>Final Amount</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative', zIndex: 1 }}>
          <IndianRupee size={16} color={theme.primary} style={{ fontWeight: '700' }} />
          <input
            type="number"
            value={parseFloat(orderData.amounts.finalAmount).toFixed(2)}
            onChange={(e) => handleAmountChange('finalAmount', e.target.value)}
            style={{
              fontSize: '20px',
              fontWeight: '900',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `2.5px solid ${theme.primary}`,
              color: theme.primary,
              outline: 'none',
              width: '95px',
              textAlign: 'right',
              padding: '4px 6px',
              letterSpacing: '-0.5px',
              transition: 'all 0.2s ease',
            }}
            onFocus={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.borderBottomWidth = '3px';
            }}
            onBlur={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.borderBottomWidth = '2.5px';
            }}
          />
        </div>
      </div>
    </div>

    {/* ✅ Business Amount - SEPARATE PROMINENT ROW (Additional Breakdown से ऊपर) */}
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 18px',
      borderTop: `1px solid ${theme.border}`,
      background: `linear-gradient(135deg, ${theme.secondary}12 0%, ${theme.secondary}06 100%)`,
      transition: 'all 0.2s ease',
      cursor: 'text',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = `linear-gradient(135deg, ${theme.secondary}18 0%, ${theme.secondary}10 100%)`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = `linear-gradient(135deg, ${theme.secondary}12 0%, ${theme.secondary}06 100%)`;
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '38px',
          height: '38px',
          borderRadius: '10px',
          background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.accent} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 12px ${theme.secondary}35`,
        }}>
          <Store size={18} color="#fff" />
        </div>
        <div>
          <div style={{ fontSize: '10px', fontWeight: '700', color: theme.secondary, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' }}>
            Business Earnings
          </div>
          <span style={{ fontSize: '14px', color: theme.text, fontWeight: '700' }}>Business Amount</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <IndianRupee size={16} color={theme.secondary} style={{ fontWeight: '700' }} />
        <input
          type="number"
          value={orderData.amounts.businessAmount}
          onChange={(e) => handleAmountChange('businessAmount', e.target.value)}
          style={{
            fontSize: '18px',
            fontWeight: '800',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `2px solid ${theme.secondary}`,
            color: theme.secondary,
            outline: 'none',
            width: '100px',
            textAlign: 'right',
            padding: '4px 6px',
            letterSpacing: '-0.3px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.borderBottomWidth = '2.5px';
          }}
          onBlur={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.borderBottomWidth = '2px';
          }}
        />
      </div>
    </div>

    {/* Divider with Label */}
    <div style={{
      padding: '12px 18px',
      backgroundColor: theme.surfaceAlt,
      borderTop: `1px solid ${theme.border}`,
      borderBottom: `1px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}>
      <div style={{
        width: '4px',
        height: '16px',
        borderRadius: '2px',
        background: `linear-gradient(180deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
      }} />
      <span style={{ fontSize: '12px', fontWeight: '700', color: theme.text, textTransform: 'uppercase', letterSpacing: '1px' }}>
        Additional Breakdown
      </span>
    </div>

    {/* Additional Amounts Grid */}
    <div style={{
      padding: '16px',
      backgroundColor: theme.surface,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: '10px',
    }}>
      {/* Commission */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.warning + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.warning}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Star size={13} color={theme.warning} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>Commission</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.commissionAmount || orderData.amounts.zaptodoorCommission}
          onChange={(e) => handleAmountChange('commissionAmount', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.warning;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Handling Fee Amount */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.info + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.info}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Package size={13} color={theme.info} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>Handling Fee</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.handlingFeeAmount}
          onChange={(e) => handleAmountChange('handlingFeeAmount', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.info;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Receiving Fee Amount */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.success + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.success}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Receipt size={13} color={theme.success} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>Receiving Fee</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.receivingFeeAmount}
          onChange={(e) => handleAmountChange('receivingFeeAmount', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.success;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* User Fee */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.info + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.info}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <User size={13} color={theme.info} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>User Fee</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.userPlatformFee}
          onChange={(e) => handleAmountChange('userPlatformFee', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.info;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Driver Fee */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.success + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.success}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Bike size={13} color={theme.success} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>Driver Fee</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.driverPlatformFee}
          onChange={(e) => handleAmountChange('driverPlatformFee', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.success;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Biz Fee */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.secondary + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.secondary}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Store size={13} color={theme.secondary} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>Biz Fee</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.bizPlatformFee}
          onChange={(e) => handleAmountChange('bizPlatformFee', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.secondary;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Driver Amount - HIGHLIGHTED */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        background: `linear-gradient(135deg, ${theme.success}12 0%, ${theme.success}06 100%)`,
        border: `1.5px solid ${theme.success}30`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.success + '60';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 6px 14px ${theme.success}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.success + '30';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            background: `linear-gradient(135deg, ${theme.success} 0%, ${theme.successLight} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 3px 8px ${theme.success}30`,
          }}>
            <Bike size={12} color="#fff" />
          </div>
          <span style={{ fontSize: '12px', fontWeight: '700', color: theme.success }}>Driver Amount</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.driverAmount}
          onChange={(e) => handleAmountChange('driverAmount', e.target.value)}
          style={{
            fontSize: '14px',
            fontWeight: '800',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `2px solid ${theme.success}`,
            color: theme.success,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.transform = 'scale(1.05)';
          }}
          onBlur={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        />
      </div>

      {/* Driver Tips */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.accent + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.accent}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Heart size={13} color={theme.accent} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>Driver Tips</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.driverTips}
          onChange={(e) => handleAmountChange('driverTips', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.accent;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>

      {/* Wallet Pay */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: '10px',
        backgroundColor: theme.surfaceAlt,
        border: `1px solid ${theme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.primary + '40';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = `0 4px 10px ${theme.primary}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Wallet size={13} color={theme.primary} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>Wallet Pay</span>
        </div>
        <input
          type="number"
          value={orderData.amounts.walletPay}
          onChange={(e) => handleAmountChange('walletPay', e.target.value)}
          style={{
            fontSize: '13px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: `1.5px dashed ${theme.border}`,
            color: theme.text,
            outline: 'none',
            width: '75px',
            textAlign: 'right',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderBottomColor = theme.primary;
            e.target.style.borderBottomStyle = 'solid';
          }}
          onBlur={(e) => {
            e.target.style.borderBottomColor = theme.border;
            e.target.style.borderBottomStyle = 'dashed';
          }}
        />
      </div>
    </div>
  </div>
</SectionHeader>

          {/* ============================================================ */}
          {/* ITEMS SECTION - WITH IMAGES */}
          {/* ============================================================ */}
          <SectionHeader title="Items" icon={Utensils} section="items">
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '12px', color: theme.textMuted }}>{orderData.items.length} items</span>
                <button
                  type="button"
                  onClick={addItem}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: theme.primary,
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'opacity 0.15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <Plus size={14} />
                  Add Item
                </button>
              </div>
              {orderData.items.map((item, index) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderBottom: index < orderData.items.length - 1 ? `1px solid ${theme.border}` : 'none',
                    gap: '12px',
                    flexWrap: 'wrap',
                    backgroundColor: theme.surfaceAlt,
                    borderRadius: '8px',
                    marginBottom: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    {/* Item Image */}
                    {item.image ? (
                      <img
                        src={item.image.startsWith('http') ? item.image : `${serverURL}${item.image}`}
                        alt={item.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          border: `1px solid ${theme.border}`,
                          backgroundColor: theme.surface,
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <span style="font-size:28px;">${getItemImage(item.name)}</span>
                          `;
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '28px' }}>{getItemImage(item.name)}</span>
                    )}
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderBottom: `1px dashed ${theme.border}`,
                          color: theme.text,
                          outline: 'none',
                          width: '100%',
                          padding: '2px 4px',
                        }}
                        placeholder="Item name"
                      />
                      {item.variant && (
                        <span style={{ fontSize: '11px', color: theme.textMuted, marginTop: '2px', display: 'block' }}>
                          {item.variant}
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: '10px', marginTop: '4px', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '11px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Qty:
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', Number(e.target.value))}
                            style={{
                              width: '45px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.surface,
                              color: theme.text,
                              fontSize: '11px',
                              outline: 'none',
                            }}
                            min="1"
                          />
                        </label>
                        <label style={{ fontSize: '11px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ₹
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => updateItem(item.id, 'price', Number(e.target.value))}
                            style={{
                              width: '60px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.surface,
                              color: theme.text,
                              fontSize: '11px',
                              outline: 'none',
                            }}
                            min="0"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: theme.primary }}>
                      ₹{item.total}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      style={{
                        padding: '4px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: theme.danger + '12',
                        color: theme.danger,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.danger;
                        e.currentTarget.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.danger + '12';
                        e.currentTarget.style.color = theme.danger;
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </SectionHeader>

          {/* ============================================================ */}
          {/* PEOPLE SECTION - WITH RESTAURANT & DRIVER CONTACT */}
          {/* ============================================================ */}
          <SectionHeader title="People" icon={Users} section="people">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
              {/* Customer */}
              <div style={{
                padding: '14px',
                borderRadius: '10px',
                backgroundColor: theme.surfaceAlt,
                border: `1px solid ${theme.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: theme.primary + '15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.primary,
                  }}>
                    <User size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Customer
                    </div>
                    <input
                      type="text"
                      value={orderData.people.customer.name}
                      onChange={(e) => handleCustomerChange('name', e.target.value)}
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: `1px dashed ${theme.border}`,
                        color: theme.text,
                        outline: 'none',
                        width: '100%',
                        padding: '2px 4px',
                      }}
                      placeholder="Customer name"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={orderData.people.customer.username}
                    onChange={(e) => handleCustomerChange('username', e.target.value)}
                    style={{
                      fontSize: '11px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: `1px dashed ${theme.border}`,
                      color: theme.textMuted,
                      outline: 'none',
                      flex: 1,
                      padding: '2px 4px',
                    }}
                    placeholder="@username"
                  />
                  <input
                    type="text"
                    value={orderData.people.customer.phone}
                    onChange={(e) => handleCustomerChange('phone', e.target.value)}
                    style={{
                      fontSize: '11px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: `1px dashed ${theme.border}`,
                      color: theme.textMuted,
                      outline: 'none',
                      flex: 1,
                      padding: '2px 4px',
                    }}
                    placeholder="Phone"
                  />
                </div>
              </div>

              {/* Restaurant - WITH CONTACT */}
              <div style={{
                padding: '14px',
                borderRadius: '10px',
                backgroundColor: theme.surfaceAlt,
                border: `1px solid ${theme.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: theme.warning + '15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.warning,
                  }}>
                    <Store size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Restaurant
                    </div>
                    <input
                      type="text"
                      value={orderData.people.restaurant.name}
                      onChange={(e) => handleRestaurantChange('name', e.target.value)}
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: `1px dashed ${theme.border}`,
                        color: theme.text,
                        outline: 'none',
                        width: '100%',
                        padding: '2px 4px',
                      }}
                      placeholder="Restaurant name"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={orderData.people.restaurant.id}
                    onChange={(e) => handleRestaurantChange('id', e.target.value)}
                    style={{
                      fontSize: '11px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: `1px dashed ${theme.border}`,
                      color: theme.textMuted,
                      outline: 'none',
                      flex: 1,
                      padding: '2px 4px',
                    }}
                    placeholder="Restaurant ID"
                  />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    backgroundColor: theme.success + '10',
                    border: `1px solid ${theme.success}20`,
                    flex: 1,
                  }}>
                    <Phone size={12} color={theme.success} />
                    <input
                      type="text"
                      value={orderData.people.restaurant.contact}
                      onChange={(e) => handleRestaurantChange('contact', e.target.value)}
                      style={{
                        fontSize: '11px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: theme.success,
                        outline: 'none',
                        flex: 1,
                        fontWeight: '600',
                        padding: '2px 4px',
                      }}
                      placeholder="Restaurant Contact"
                    />
                  </div>
                </div>
              </div>

              {/* Driver - WITH CONTACT */}
              <div style={{
                padding: '14px',
                borderRadius: '10px',
                backgroundColor: theme.surfaceAlt,
                border: `1px solid ${theme.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: theme.success + '15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.success,
                  }}>
                    <Bike size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Driver
                    </div>
                    <input
                      type="text"
                      value={orderData.people.driver.name}
                      onChange={(e) => handleDriverChange('name', e.target.value)}
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: `1px dashed ${theme.border}`,
                        color: theme.text,
                        outline: 'none',
                        width: '100%',
                        padding: '2px 4px',
                      }}
                      placeholder="Driver name"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={orderData.people.driver.id}
                    onChange={(e) => handleDriverChange('id', e.target.value)}
                    style={{
                      fontSize: '11px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: `1px dashed ${theme.border}`,
                      color: theme.textMuted,
                      outline: 'none',
                      flex: 1,
                      padding: '2px 4px',
                    }}
                    placeholder="Driver ID"
                  />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    backgroundColor: theme.primary + '10',
                    border: `1px solid ${theme.primary}20`,
                    flex: 1,
                  }}>
                    <Phone size={12} color={theme.primary} />
                    <input
                      type="text"
                      value={orderData.people.driver.contact}
                      onChange={(e) => handleDriverChange('contact', e.target.value)}
                      style={{
                        fontSize: '11px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: theme.primary,
                        outline: 'none',
                        flex: 1,
                        fontWeight: '600',
                        padding: '2px 4px',
                      }}
                      placeholder="Driver Contact"
                    />
                  </div>
                </div>
              </div>
            </div>
          </SectionHeader>

          {/* ============================================================ */}
          {/* DELIVERY SECTION - WITH IMAGES */}
          {/* ============================================================ */}
          <SectionHeader title="Delivery Details" icon={Truck} section="delivery">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {/* Pickup */}
              <div>
                <div style={{
                  padding: '12px',
                  borderRadius: '10px',
                  backgroundColor: theme.surfaceAlt,
                  border: `1px solid ${theme.border}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: theme.success }} />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: theme.text }}>Pickup Address</span>
                  </div>
                  <textarea
                    value={orderData.delivery.pickup}
                    onChange={(e) => handleDeliveryChange('pickup', e.target.value)}
                    style={{
                      fontSize: '12px',
                      color: theme.textSub,
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: `1px dashed ${theme.border}`,
                      outline: 'none',
                      width: '100%',
                      padding: '4px',
                      resize: 'vertical',
                      lineHeight: '1.5',
                      fontFamily: 'inherit',
                    }}
                    rows="2"
                    placeholder="Pickup address"
                  />
                </div>
                {/* Pickup Image */}
                <div style={{ marginTop: '10px' }}>
                  {renderImage(
                    orderData.delivery.pickupPic,
                    <Camera size={32} color={theme.textMuted} />,
                    'Pickup Image'
                  )}
                </div>
              </div>

              {/* Dropoff */}
              <div>
                <div style={{
                  padding: '12px',
                  borderRadius: '10px',
                  backgroundColor: theme.surfaceAlt,
                  border: `1px solid ${theme.border}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: theme.danger }} />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: theme.text }}>Dropoff Address</span>
                  </div>
                  <textarea
                    value={orderData.delivery.dropoff}
                    onChange={(e) => handleDeliveryChange('dropoff', e.target.value)}
                    style={{
                      fontSize: '12px',
                      color: theme.textSub,
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: `1px dashed ${theme.border}`,
                      outline: 'none',
                      width: '100%',
                      padding: '4px',
                      resize: 'vertical',
                      lineHeight: '1.5',
                      fontFamily: 'inherit',
                    }}
                    rows="2"
                    placeholder="Dropoff address"
                  />
                </div>
                {/* Dropoff Image */}
                <div style={{ marginTop: '10px' }}>
                  {renderImage(
                    orderData.delivery.dropoffPic,
                    <Camera size={32} color={theme.textMuted} />,
                    'Dropoff Image'
                  )}
                </div>
              </div>
            </div>
          </SectionHeader>

          {/* ============================================================ */}
          {/* TIMELINE SECTION - HORIZONTAL */}
          {/* ============================================================ */}
          <SectionHeader title="Timeline" icon={Clock} section="timeline">
            <div style={{
              overflowX: 'auto',
              padding: '8px 4px 16px 4px',
              position: 'relative',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0',
                minWidth: '750px',
                position: 'relative',
                paddingTop: '20px',
              }}>
                {/* Horizontal Line */}
                <div style={{
                  position: 'absolute',
                  top: '38px',
                  left: '30px',
                  right: '30px',
                  height: '3px',
                  backgroundColor: theme.border,
                  zIndex: 0,
                }} />

                {timelineEvents.map((event, index) => {
                  const isCompleted = event.status === 'completed';
                  const isActive = event.status === 'active';
                  const Icon = event.icon;
                  const isLast = index === timelineEvents.length - 1;

                  const lineColor = isCompleted ? theme.success : isActive ? theme.warning : theme.border;

                  return (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                        minWidth: '100px',
                      }}
                    >
                      {/* Status Dot with Icon */}
                      <div
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          backgroundColor: isCompleted
                            ? theme.success
                            : isActive
                            ? theme.warning
                            : theme.surfaceAlt,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `3px solid ${
                            isCompleted
                              ? theme.success
                              : isActive
                              ? theme.warning
                              : theme.border
                          }`,
                          boxShadow: isCompleted
                            ? `0 0 0 6px ${theme.success}20`
                            : isActive
                            ? `0 0 0 6px ${theme.warning}20`
                            : 'none',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          position: 'relative',
                          zIndex: 2,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                          e.currentTarget.style.boxShadow = isCompleted
                            ? `0 0 0 8px ${theme.success}30`
                            : isActive
                            ? `0 0 0 8px ${theme.warning}30`
                            : `0 0 0 8px ${theme.border}30`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = isCompleted
                            ? `0 0 0 6px ${theme.success}20`
                            : isActive
                            ? `0 0 0 6px ${theme.warning}20`
                            : 'none';
                        }}
                      >
                        <Icon
                          size={18}
                          color={
                            isCompleted || isActive
                              ? '#fff'
                              : theme.textMuted
                          }
                        />
                      </div>

                      {/* Label */}
                      <div
                        style={{
                          marginTop: '10px',
                          fontSize: '12px',
                          fontWeight: isCompleted || isActive ? '700' : '500',
                          color: isCompleted
                            ? theme.success
                            : isActive
                            ? theme.warning
                            : theme.textMuted,
                          textAlign: 'center',
                          lineHeight: '1.3',
                          maxWidth: '110px',
                        }}
                      >
                        {event.label}
                      </div>

                      {/* Time */}
                      <div
                        style={{
                          marginTop: '4px',
                          fontSize: '10px',
                          color: theme.textMuted,
                          textAlign: 'center',
                          fontFamily: 'monospace',
                          background: theme.surfaceAlt,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          border: `1px solid ${theme.border}`,
                          maxWidth: '120px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={event.time}
                      >
                        {event.time === 'Pending' || event.time === 'N/A' ? (
                          <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>
                            {event.time}
                          </span>
                        ) : (
                          event.time
                        )}
                      </div>

                      {/* Status Badge */}
                      <div
                        style={{
                          marginTop: '6px',
                          fontSize: '8px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          padding: '2px 10px',
                          borderRadius: '10px',
                          backgroundColor: isCompleted
                            ? theme.success + '15'
                            : isActive
                            ? theme.warning + '15'
                            : theme.surfaceAlt,
                          color: isCompleted
                            ? theme.success
                            : isActive
                            ? theme.warning
                            : theme.textMuted,
                          border: `1px solid ${
                            isCompleted
                              ? theme.success + '30'
                              : isActive
                              ? theme.warning + '30'
                              : theme.border
                          }`,
                        }}
                      >
                        {isCompleted ? '✓ Done' : isActive ? '⏳ In Progress' : '⏸ Pending'}
                      </div>

                      {/* Connecting Line (except last) */}
                      {!isLast && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '43px',
                            right: '-50%',
                            height: '3px',
                            width: '100%',
                            backgroundColor: lineColor,
                            zIndex: 0,
                            opacity: 0.6,
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </SectionHeader>

          {/* ============================================================ */}
          {/* ACTION BUTTONS */}
          {/* ============================================================ */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <button
              type="button"
              onClick={() => navigate('/Dashboard/orders')}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                border: `1px solid ${theme.border}`,
                backgroundColor: 'transparent',
                color: theme.text,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.surfaceAlt;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '14px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: saving ? theme.textMuted : theme.primary,
                color: '#fff',
                fontSize: '15px',
                fontWeight: '700',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.3)';
                }
              }}
            >
              {saving ? (
                <>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default OrderDetailPage;